// Parameters Store - Centralized parameter management with caching

import { create } from "zustand";
import ParametersRepository from "@/core/parameters/repositories/ParametersRepository";
import {
  AccountPurpose,
  Civility,
  Country,
  DistributionChannel,
  FamilyStatus,
  IncomeSource,
  Nationality,
  ProductOption,
  ProfessionCategory,
  Profession,
  SectorActivity,
  Agency,
} from "@/core/parameters/entities/ParameterTypes";

interface ParametersState {
  // Product Options state
  productOptions: ProductOption[];
  productOptionsLoading: boolean;
  productOptionsError: string | null;
  productOptionsLoaded: boolean;

  // Civilities state
  civilities: Civility[];
  civilitiesLoading: boolean;
  civilitiesError: string | null;
  civilitiesLoaded: boolean;

  // Countries state
  countries: Country[];
  countriesLoading: boolean;
  countriesError: string | null;
  countriesLoaded: boolean;

  // Family Status state
  familyStatus: FamilyStatus[];
  familyStatusLoading: boolean;
  familyStatusError: string | null;
  familyStatusLoaded: boolean;

  // Nationalities state
  nationalities: Nationality[];
  nationalitiesLoading: boolean;
  nationalitiesError: string | null;
  nationalitiesLoaded: boolean;

  // Profession Categories state
  professionCategories: ProfessionCategory[];
  professionCategoriesLoading: boolean;
  professionCategoriesError: string | null;
  professionCategoriesLoaded: boolean;

  // Professions state
  professions: Profession[];
  professionsLoading: boolean;
  professionsError: string | null;
  professionsLoaded: boolean;

  // Agencies state
  agencies: Agency[];
  agenciesLoading: boolean;
  agenciesError: string | null;
  agenciesLoaded: boolean;

  // Account Purposes state
  accountPurposes: AccountPurpose[];
  accountPurposesLoading: boolean;
  accountPurposesError: string | null;
  accountPurposesLoaded: boolean;

  // Income Sources state
  incomeSources: IncomeSource[];
  incomeSourcesLoading: boolean;
  incomeSourcesError: string | null;
  incomeSourcesLoaded: boolean;

  // Distribution Channels state
  distributionChannels: DistributionChannel[];
  distributionChannelsLoading: boolean;
  distributionChannelsError: string | null;
  distributionChannelsLoaded: boolean;

  // Sector Activities state
  sectorActivities: SectorActivity[];
  sectorActivitiesLoading: boolean;
  sectorActivitiesError: string | null;
  sectorActivitiesLoaded: boolean;

  // Actions
  loadProductOptions: () => Promise<void>;
  loadCivilities: () => Promise<void>;
  loadCountries: () => Promise<void>;
  loadFamilyStatus: () => Promise<void>;
  loadNationalities: () => Promise<void>;
  loadProfessionCategories: () => Promise<void>;
  loadProfessions: () => Promise<void>;
  loadAgencies: () => Promise<void>;
  loadAccountPurposes: () => Promise<void>;
  loadIncomeSources: () => Promise<void>;
  loadDistributionChannels: () => Promise<void>;
  loadSectorActivities: () => Promise<void>;
  loadAllParameters: () => Promise<void>;
  resetState: () => void;
}

// Repository instance (will be injected via ServiceContainer)
let parametersRepository: ParametersRepository | undefined;

export const setParametersRepository = (repository: ParametersRepository) => {
  parametersRepository = repository;
};

const initialState = {
  productOptions: [],
  productOptionsLoading: false,
  productOptionsError: null,
  productOptionsLoaded: false,

  civilities: [],
  civilitiesLoading: false,
  civilitiesError: null,
  civilitiesLoaded: false,

  countries: [],
  countriesLoading: false,
  countriesError: null,
  countriesLoaded: false,

  familyStatus: [],
  familyStatusLoading: false,
  familyStatusError: null,
  familyStatusLoaded: false,

  nationalities: [],
  nationalitiesLoading: false,
  nationalitiesError: null,
  nationalitiesLoaded: false,

  professionCategories: [],
  professionCategoriesLoading: false,
  professionCategoriesError: null,
  professionCategoriesLoaded: false,

  professions: [],
  professionsLoading: false,
  professionsError: null,
  professionsLoaded: false,

  agencies: [],
  agenciesLoading: false,
  agenciesError: null,
  agenciesLoaded: false,

  accountPurposes: [],
  accountPurposesLoading: false,
  accountPurposesError: null,
  accountPurposesLoaded: false,

  incomeSources: [],
  incomeSourcesLoading: false,
  incomeSourcesError: null,
  incomeSourcesLoaded: false,

  distributionChannels: [],
  distributionChannelsLoading: false,
  distributionChannelsError: null,
  distributionChannelsLoaded: false,

  sectorActivities: [],
  sectorActivitiesLoading: false,
  sectorActivitiesError: null,
  sectorActivitiesLoaded: false,
};

export const useParametersStore = create<ParametersState>((set, get) => ({
  ...initialState,

  loadProductOptions: async () => {
    if (!parametersRepository) {
      throw new Error(
        "ParametersRepository not initialized. Call setParametersRepository first.",
      );
    }

    const state = get();
    // Skip if already loaded or currently loading
    if (state.productOptionsLoaded || state.productOptionsLoading) {
      return;
    }

    set({ productOptionsLoading: true, productOptionsError: null });

    try {
      const response = await parametersRepository.getProductOptions();
      set({
        productOptions: response.data || [],
        productOptionsLoading: false,
        productOptionsLoaded: true,
        productOptionsError: null,
      });
    } catch (error) {
      set({
        productOptionsLoading: false,
        productOptionsError:
          error instanceof Error
            ? error.message
            : "Failed to load product options",
      });
    }
  },

  loadCivilities: async () => {
    if (!parametersRepository) {
      throw new Error(
        "ParametersRepository not initialized. Call setParametersRepository first.",
      );
    }

    const state = get();
    // Skip if already loaded or currently loading
    if (state.civilitiesLoaded || state.civilitiesLoading) {
      return;
    }

    set({ civilitiesLoading: true, civilitiesError: null });

    try {
      const response = await parametersRepository.getCivilities();
      set({
        civilities: response.data || [],
        civilitiesLoading: false,
        civilitiesLoaded: true,
        civilitiesError: null,
      });
    } catch (error) {
      set({
        civilitiesLoading: false,
        civilitiesError:
          error instanceof Error ? error.message : "Failed to load civilities",
      });
    }
  },

  loadCountries: async () => {
    if (!parametersRepository) {
      throw new Error(
        "ParametersRepository not initialized. Call setParametersRepository first.",
      );
    }

    const state = get();
    // Skip if already loaded or currently loading
    if (state.countriesLoaded || state.countriesLoading) {
      return;
    }

    set({ countriesLoading: true, countriesError: null });

    try {
      const response = await parametersRepository.getCountries();
      set({
        countries: response.data || [],
        countriesLoading: false,
        countriesLoaded: true,
        countriesError: null,
      });
    } catch (error) {
      set({
        countriesLoading: false,
        countriesError:
          error instanceof Error ? error.message : "Failed to load countries",
      });
    }
  },

  loadFamilyStatus: async () => {
    if (!parametersRepository) {
      throw new Error(
        "ParametersRepository not initialized. Call setParametersRepository first.",
      );
    }

    const state = get();
    // Skip if already loaded or currently loading
    if (state.familyStatusLoaded || state.familyStatusLoading) {
      return;
    }

    set({ familyStatusLoading: true, familyStatusError: null });

    try {
      const response = await parametersRepository.getFamilyStatus();
      set({
        familyStatus: response.data || [],
        familyStatusLoading: false,
        familyStatusLoaded: true,
        familyStatusError: null,
      });
    } catch (error) {
      set({
        familyStatusLoading: false,
        familyStatusError:
          error instanceof Error
            ? error.message
            : "Failed to load family status",
      });
    }
  },

  loadNationalities: async () => {
    if (!parametersRepository) {
      throw new Error(
        "ParametersRepository not initialized. Call setParametersRepository first.",
      );
    }

    const state = get();
    // Skip if already loaded or currently loading
    if (state.nationalitiesLoaded || state.nationalitiesLoading) {
      return;
    }

    set({ nationalitiesLoading: true, nationalitiesError: null });

    try {
      const response = await parametersRepository.getNationalities();
      set({
        nationalities: response.data || [],
        nationalitiesLoading: false,
        nationalitiesLoaded: true,
        nationalitiesError: null,
      });
    } catch (error) {
      set({
        nationalitiesLoading: false,
        nationalitiesError:
          error instanceof Error
            ? error.message
            : "Failed to load nationalities",
      });
    }
  },

  loadProfessionCategories: async () => {
    if (!parametersRepository) {
      throw new Error(
        "ParametersRepository not initialized. Call setParametersRepository first.",
      );
    }

    const state = get();
    // Skip if already loaded or currently loading
    if (state.professionCategoriesLoaded || state.professionCategoriesLoading) {
      return;
    }

    set({ professionCategoriesLoading: true, professionCategoriesError: null });

    try {
      const response = await parametersRepository.getProfessionCategories();
      set({
        professionCategories: response.data || [],
        professionCategoriesLoading: false,
        professionCategoriesLoaded: true,
        professionCategoriesError: null,
      });
    } catch (error) {
      set({
        professionCategoriesLoading: false,
        professionCategoriesError:
          error instanceof Error
            ? error.message
            : "Failed to load profession categories",
      });
    }
  },

  loadProfessions: async () => {
    if (!parametersRepository) {
      throw new Error(
        "ParametersRepository not initialized. Call setParametersRepository first.",
      );
    }

    const state = get();
    // Skip if already loaded or currently loading
    if (state.professionsLoaded || state.professionsLoading) {
      return;
    }

    set({ professionsLoading: true, professionsError: null });

    try {
      const response = await parametersRepository.getProfessions();
      set({
        professions: response.data || [],
        professionsLoading: false,
        professionsLoaded: true,
        professionsError: null,
      });
    } catch (error) {
      set({
        professionsLoading: false,
        professionsError:
          error instanceof Error ? error.message : "Failed to load professions",
      });
    }
  },

  loadAgencies: async () => {
    if (!parametersRepository) {
      throw new Error(
        "ParametersRepository not initialized. Call setParametersRepository first.",
      );
    }

    const state = get();
    // Skip if already loaded or currently loading
    if (state.agenciesLoaded || state.agenciesLoading) {
      return;
    }

    set({ agenciesLoading: true, agenciesError: null });

    try {
      const response = await parametersRepository.getAgencies();
      set({
        agencies: response.data.items || [],
        agenciesLoading: false,
        agenciesLoaded: true,
        agenciesError: null,
      });
    } catch (error) {
      set({
        agenciesLoading: false,
        agenciesError:
          error instanceof Error ? error.message : "Failed to load agencies",
      });
    }
  },

  loadAccountPurposes: async () => {
    if (!parametersRepository) {
      throw new Error(
        "ParametersRepository not initialized. Call setParametersRepository first.",
      );
    }
    const state = get();
    if (state.accountPurposesLoaded || state.accountPurposesLoading) return;
    set({ accountPurposesLoading: true, accountPurposesError: null });
    try {
      const response = await parametersRepository.getAccountPurposes();
      set({
        accountPurposes: response.data || [],
        accountPurposesLoading: false,
        accountPurposesLoaded: true,
        accountPurposesError: null,
      });
    } catch (error) {
      set({
        accountPurposesLoading: false,
        accountPurposesError:
          error instanceof Error
            ? error.message
            : "Failed to load account purposes",
      });
    }
  },

  loadIncomeSources: async () => {
    if (!parametersRepository) {
      throw new Error(
        "ParametersRepository not initialized. Call setParametersRepository first.",
      );
    }
    const state = get();
    if (state.incomeSourcesLoaded || state.incomeSourcesLoading) return;
    set({ incomeSourcesLoading: true, incomeSourcesError: null });
    try {
      const response = await parametersRepository.getIncomeSources();
      set({
        incomeSources: response.data || [],
        incomeSourcesLoading: false,
        incomeSourcesLoaded: true,
        incomeSourcesError: null,
      });
    } catch (error) {
      set({
        incomeSourcesLoading: false,
        incomeSourcesError:
          error instanceof Error
            ? error.message
            : "Failed to load income sources",
      });
    }
  },

  loadDistributionChannels: async () => {
    if (!parametersRepository) {
      throw new Error(
        "ParametersRepository not initialized. Call setParametersRepository first.",
      );
    }
    const state = get();
    if (state.distributionChannelsLoaded || state.distributionChannelsLoading) return;
    set({ distributionChannelsLoading: true, distributionChannelsError: null });
    try {
      const response = await parametersRepository.getDistributionChannels();
      set({
        distributionChannels: response.data || [],
        distributionChannelsLoading: false,
        distributionChannelsLoaded: true,
        distributionChannelsError: null,
      });
    } catch (error) {
      set({
        distributionChannelsLoading: false,
        distributionChannelsError:
          error instanceof Error
            ? error.message
            : "Failed to load distribution channels",
      });
    }
  },

  loadSectorActivities: async () => {
    if (!parametersRepository) {
      throw new Error(
        "ParametersRepository not initialized. Call setParametersRepository first.",
      );
    }
    const state = get();
    if (state.sectorActivitiesLoaded || state.sectorActivitiesLoading) return;
    set({ sectorActivitiesLoading: true, sectorActivitiesError: null });
    try {
      const response = await parametersRepository.getSectorActivities();
      set({
        sectorActivities: response.data || [],
        sectorActivitiesLoading: false,
        sectorActivitiesLoaded: true,
        sectorActivitiesError: null,
      });
    } catch (error) {
      set({
        sectorActivitiesLoading: false,
        sectorActivitiesError:
          error instanceof Error
            ? error.message
            : "Failed to load sector activities",
      });
    }
  },

  loadAllParameters: async () => {
    if (!parametersRepository) {
      throw new Error(
        "ParametersRepository not initialized. Call setParametersRepository first.",
      );
    }

    const state = get();
    const toLoad: Promise<void>[] = [];
    const enqueueIfNeeded = (
      loaded: boolean,
      loading: boolean,
      loader: () => Promise<void>,
    ) => {
      if (!loaded && !loading) {
        toLoad.push(loader());
      }
    };

    enqueueIfNeeded(
      state.productOptionsLoaded,
      state.productOptionsLoading,
      get().loadProductOptions,
    );
    enqueueIfNeeded(state.civilitiesLoaded, state.civilitiesLoading, get().loadCivilities);
    enqueueIfNeeded(state.countriesLoaded, state.countriesLoading, get().loadCountries);
    enqueueIfNeeded(
      state.familyStatusLoaded,
      state.familyStatusLoading,
      get().loadFamilyStatus,
    );
    enqueueIfNeeded(
      state.nationalitiesLoaded,
      state.nationalitiesLoading,
      get().loadNationalities,
    );
    enqueueIfNeeded(state.professionsLoaded, state.professionsLoading, get().loadProfessions);
    enqueueIfNeeded(state.agenciesLoaded, state.agenciesLoading, get().loadAgencies);
    enqueueIfNeeded(
      state.accountPurposesLoaded,
      state.accountPurposesLoading,
      get().loadAccountPurposes,
    );
    enqueueIfNeeded(
      state.incomeSourcesLoaded,
      state.incomeSourcesLoading,
      get().loadIncomeSources,
    );
    enqueueIfNeeded(
      state.distributionChannelsLoaded,
      state.distributionChannelsLoading,
      get().loadDistributionChannels,
    );
    enqueueIfNeeded(
      state.sectorActivitiesLoaded,
      state.sectorActivitiesLoading,
      get().loadSectorActivities,
    );

    // Load all parameters in parallel
    if (toLoad.length > 0) {
      await Promise.all(toLoad);
    }
  },

  resetState: () => {
    set(initialState);
  },
}));
